<script setup lang="ts">
import { SEARCH_FILTERS, type EstablishmentSearchResult, type SearchEstablishmentsResponse, type SearchFilters, type MapPoint, type FilterStats } from '~~/shared/search-constants'
import {
  ESTABLISHMENT_FILTER_I18N_KEYS,
  isRegionFilterSlug,
  isNationalFilterSlug,
  type EstablishmentFilterSlug,
} from '~~/shared/establishment-filters'
import { buildListingPostalAddress, mapEquipmentsToSchemaOrg } from '~/utils/schema-org-mappings'
import { ficheLinkQuery, guestApiQuery, guestRouteQuery, parseGuestQuery, pseoBarModel, sameDates, sameGuests, splitPseoBarUpdate, type GuestQuery, type PseoZone } from '~/utils/pseo-guest-query'

/**
 * Étage régional du filtre pSEO (spec 4.68.0) : `/regions/{region}/filtre/{filtre}`.
 *
 * Frère de `app/pages/[codeSlug]/filtre/[filter].vue`, dont il reprend la
 * structure à l'échelle du territoire régional. Deux écarts assumés :
 *
 * - **Pas de `TouristDestination`.** `REGIONS` ne porte pas de `boundingBox` ;
 *   une emprise géographique inventée serait une donnée fausse envoyée à Google.
 * - **Pas d'overlay éditorial DB.** Aucun `BlogPost PAGE_FILTER` n'est indexé sur
 *   un couple région × filtre ; le jour où ce besoin apparaît, la convention de
 *   `targetSlug` reste à définir.
 *
 * FR uniquement dans ce lot, comme l'étage national.
 */

interface FilterCountByFilter { filter: EstablishmentFilterSlug; count: number }
interface FilterCountByDept { departmentCode: string; departmentName: string | null; count: number }

const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const config = useRuntimeConfig()
const { getRegionBySlug } = useRegionsData()

// FR seul, et de façon EFFECTIVE. Sans cette restriction, la stratégie
// `prefix_and_default` génère aussi `/en/regions/{region}/filtre/{filtre}` : la
// route existerait, passerait `validate`, rendrait les clés i18n brutes faute de
// catalogue anglais — et serait servie en `index, follow`. Elle ferait de surcroît
// émettre un `alternate hreflang="en"` par `useLocaleHead` vers cette page cassée.
false && /*#__PURE__*/ defineI18nRoute({
  locales: ['fr'],
})

definePageMeta({
  layout: 'default',
  // Double garde : slug de région connu ET slug de filtre éligible au régional.
  // Les autres retombent en 404 propre, sans requête DB. `petit-dejeuner-inclus`
  // est un slug VALIDE mais exclu ici : contrairement au national, pas de 301 —
  // ces URLs n'ont jamais été publiées ni indexées, et aucun lien du site n'y
  // mène (les cinq points de maillage filtrent sur `isRegionFilterSlug`).
  validate: (route) => {
    const { isValidSlug } = useRegionsData()
    return isValidSlug(route.params.slug as string)
      && isRegionFilterSlug(route.params.filter as string)
  },
})

const regionSlug = computed(() => route.params.slug as string)
const filterSlug = computed(() => route.params.filter as EstablishmentFilterSlug)
const filterI18nKey = computed(() => ESTABLISHMENT_FILTER_I18N_KEYS[filterSlug.value])

// `validate` a déjà écarté les slugs inconnus ; ce garde couvre le cas d'une
// navigation client vers une route dont les params changent sans re-validation.
const region = computed(() => getRegionBySlug(regionSlug.value))
if (!region.value) {
  throw createError({ statusCode: 404, statusMessage: 'Region not found' })
}
const regionName = computed(() => region.value?.name ?? '')

/**
 * `?page=N` n'est produit par aucun lien de la page (la pagination est un
 * « charger plus » côté client), mais un crawler peut fabriquer l'URL. La servir
 * avec le contenu de la page 1 dupliquerait le contenu sous N adresses ; on
 * l'honore donc réellement, et le robots la sort de l'index (voir plus bas).
 */
const requestedPage = computed(() => {
  const raw = Number(route.query.page ?? 1)
  return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1
})

/**
 * Filtre de type d'hébergement (spec 4.74.0) — cf. le commentaire détaillé sur
 * `app/pages/[codeSlug]/filtre/[filter].vue`. Défaut `'all'` : le parc de
 * référence de la page, celui que comptent le `<h1>`, le sitemap et le maillage.
 */
const VALID_TYPES = ['all', 'chambre-hotes', 'gite'] as const
type TypeValue = (typeof VALID_TYPES)[number]
function parseType(raw: unknown): TypeValue {
  const v = String(raw ?? '')
  return (VALID_TYPES as readonly string[]).includes(v) ? (v as TypeValue) : 'all'
}
const accommodationType = ref<TypeValue>(parseType(route.query.type))

// Voyageurs (CAP-2) : lus depuis la query, validés, ignorés si invalides.
const guests = ref<GuestQuery>(parseGuestQuery(route.query))

const searchDates = ref<{ start: string, end: string } | null>(null)

// Lien des vignettes vers la fiche : dates, voyageurs et type de la page. Sans
// les voyageurs, la fiche calculait pour ses 2 adultes par défaut.
const ficheQuery = computed(() => ficheLinkQuery({ type: accommodationType.value, dates: searchDates.value, guests: guests.value }))

/**
 * L'API rejette en 400 tout ce qui n'est pas `YYYY-MM-DD`. Relayer la query
 * telle quelle ferait donc servir une page d'erreur sur une URL qu'un crawler
 * peut fabriquer (`?checkIn=abc`) ; on ignore silencieusement une date
 * malformée et la page rend son listing normal.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const rawCheckIn = String(route.query.checkIn ?? '')
const rawCheckOut = String(route.query.checkOut ?? '')

if (ISO_DATE.test(rawCheckIn) && ISO_DATE.test(rawCheckOut)) {
  searchDates.value = { start: rawCheckIn, end: rawCheckOut }
}

interface DepartmentItem {
  code: string
  name: string
  slug: string
  region: string
}

const [
  { data: searchResponse, status: searchStatus, error: searchError, refresh: refreshSearch },
  { data: deptResponse },
  { data: regionFilterCountsResponse },
  { data: deptCountsResponse },
] = await Promise.all([
  useAsyncData(
    `region-filter-estabs-${regionSlug.value}-${filterSlug.value}-p${requestedPage.value}`,
    () => rawFetch<SearchEstablishmentsResponse>('/api/search/establishments', {
      query: {
        // L'endpoint résout la région par nom OU par slug (cf. l.123-149) : on
        // passe le slug, seule forme stable — le nom transite sinon accentué.
        region: regionSlug.value,
        seoFilter: filterSlug.value,
        // Spec 4.74.0 — parc MIXTE aux trois étages, écrit explicitement et
        // jamais laissé au défaut de l'endpoint (cf. le même commentaire sur les
        // étages national et départemental).
        type: accommodationType.value,
        ...guestApiQuery(guests.value),
        includeStats: true,
        limit: SEARCH_FILTERS.DEFAULT_PAGE_SIZE,
        page: requestedPage.value,
        ...(searchDates.value && {
          checkIn: searchDates.value.start,
          checkOut: searchDates.value.end,
        }),
      },
    }),
  ),
  useAsyncData(
    'region-filter-departments',
    () => rawFetch<{ data: DepartmentItem[] }>('/api/departments'),
  ),
  // Maillage latéral : les autres filtres sur cette même région.
  useAsyncData(
    `region-filter-counts-all-${regionSlug.value}`,
    () => rawFetch<{ counts: FilterCountByFilter[] }>('/api/search/filter-counts', {
      query: { region: regionSlug.value },
    }),
  ),
  // Maillage descendant : ce filtre, par département de la région.
  useAsyncData(
    `region-filter-counts-dept-${regionSlug.value}-${filterSlug.value}`,
    () => rawFetch<{ counts: FilterCountByDept[] }>('/api/search/filter-counts', {
      query: { region: regionSlug.value, seoFilter: filterSlug.value },
    }),
  ),
])

// --- State ---
const establishments = ref<EstablishmentSearchResult[]>(searchResponse.value?.data ?? [])
const mapPoints = ref<MapPoint[]>(searchResponse.value?.mapPoints ?? [])
const filterStats = ref<FilterStats | null>(searchResponse.value?.filterStats ?? null)
const currentPage = ref(searchResponse.value?.meta.page ?? 1)
const hasMore = ref(searchResponse.value?.meta.hasMore ?? false)
const totalCount = ref(searchResponse.value?.meta.total ?? 0)
const loadingMore = ref(false)

const hasError = computed(() => !!searchError.value)

async function loadMore() {
  if (!hasMore.value || loadingMore.value) return
  loadingMore.value = true
  try {
    const response = await rawFetch<SearchEstablishmentsResponse>('/api/search/establishments', {
      query: {
        region: regionSlug.value,
        seoFilter: filterSlug.value,
        type: accommodationType.value,
        ...guestApiQuery(guests.value),
        limit: SEARCH_FILTERS.DEFAULT_PAGE_SIZE,
        page: currentPage.value + 1,
        ...(searchDates.value && {
          checkIn: searchDates.value.start,
          checkOut: searchDates.value.end,
        }),
      },
    })
    establishments.value = [...establishments.value, ...response.data]
    totalCount.value = response.meta.total
    hasMore.value = response.meta.hasMore
    currentPage.value = response.meta.page
  }
  catch {
    // silently fail — le bouton reste disponible pour un nouvel essai
  }
  finally {
    loadingMore.value = false
  }
}

/** Resynchronise l'état local depuis la dernière réponse de recherche. */
function syncFromSearchResponse() {
  if (!searchResponse.value) return
  establishments.value = searchResponse.value.data
  mapPoints.value = searchResponse.value.mapPoints
  filterStats.value = searchResponse.value.filterStats ?? null
  totalCount.value = searchResponse.value.meta.total
  hasMore.value = searchResponse.value.meta.hasMore
  currentPage.value = searchResponse.value.meta.page
}

// Switcher de type (spec 4.74.0) : `all` étant le défaut, il sort de l'URL au
// lieu d'y figurer. Le rafraîchissement est fait par le watcher unique ci-dessous.
function onTypeChange(value: TypeValue) {
  if (value === accommodationType.value) return
  accommodationType.value = value
}

// Watcher unique dates / type / voyageurs : la barre peut en changer plusieurs
// d'un coup ; une seule navigation `replace` (partageable, pas d'entrée
// d'historique parasite) et un seul `refreshSearch`.
watch([searchDates, accommodationType, guests], async () => {
  // Sans `page` : de nouveaux critères repartent de la page 1 (`requestedPage` lit l'URL).
  const { page: _page, ...currentQuery } = route.query
  const query = guestRouteQuery(currentQuery, guests.value)
  if (searchDates.value) {
    query.checkIn = searchDates.value.start
    query.checkOut = searchDates.value.end
  }
  else {
    delete query.checkIn
    delete query.checkOut
  }
  if (accommodationType.value === 'all') delete query.type
  else query.type = accommodationType.value
  await navigateTo({ query }, { replace: true })
  currentPage.value = 1
  await refreshSearch()
  syncFromSearchResponse()
})

// --- Maillage ---
const regionFilterCounts = computed(() => regionFilterCountsResponse.value?.counts ?? [])

/**
 * Seuil du maillage descendant, aligné sur `FILTER_PAGE_MIN_COUNT` du sitemap :
 * on ne pointe que vers des pages elles-mêmes indexables. Répliqué ici plutôt
 * qu'importé, le sitemap étant du code serveur.
 */
const LINK_MIN_COUNT = 3

const departmentLinks = computed(() => {
  const counts = deptCountsResponse.value?.counts ?? []
  const allDepts = deptResponse.value?.data ?? []
  return counts
    .map((c) => {
      const d = allDepts.find(dep => dep.code === c.departmentCode)
      if (!d) return null
      return { departmentCode: c.departmentCode, codeSlug: `${d.code}-${d.slug}`, name: d.name, count: c.count }
    })
    .filter((l): l is { departmentCode: string, codeSlug: string, name: string, count: number } => l !== null)
})

// Le composant réapplique le seuil en interne : se baser sur la liste brute
// ouvrirait un conteneur bordé pour une section vide.
const visibleDepartmentLinks = computed(() =>
  departmentLinks.value.filter(l => l.count >= LINK_MIN_COUNT),
)
// `isRegionFilterSlug` en plus du seuil : `filter-counts` renvoie les 16 filtres,
// dont les exclus du régional — les lister produirait des pills vers une 404.
const visibleSiblingFilters = computed(() =>
  regionFilterCounts.value.filter(c =>
    c.count >= LINK_MIN_COUNT
    && c.filter !== filterSlug.value
    && isRegionFilterSlug(c.filter),
  ),
)

// Remontée vers l'étage national. FR uniquement, et seulement pour les filtres
// qui ont effectivement une page nationale (`petit-dejeuner-inclus` en est exclu).
const showNationalLink = computed(() =>
  locale.value === 'fr' && isNationalFilterSlug(filterSlug.value),
)

// --- SEO ---
const ogLocale = useOgLocale()
const localePrefix = computed(() => locale.value === 'fr' ? '' : `/${locale.value}`)
const canonicalUrl = computed(() =>
  `${config.public.siteUrl}${localePrefix.value}/regions/${regionSlug.value}/filtre/${filterSlug.value}`,
)

const filterLabel = computed(() => t(`pages.regionFilter.${filterI18nKey.value}.label`))

const i18nVars = computed(() => ({
  region: regionName.value,
  count: filterStats.value?.count ?? totalCount.value,
  minPrice: filterStats.value?.priceRange?.min ?? 0,
  maxPrice: filterStats.value?.priceRange?.max ?? 0,
  avgPrice: filterStats.value?.priceRange?.avg ?? 0,
  avgRating: filterStats.value?.avgRating ?? 0,
  topCities: filterStats.value?.topCities.map(c => c.city).join(', ') ?? '',
  filterLabel: filterLabel.value,
}))

const resultCount = computed(() => filterStats.value?.count ?? totalCount.value)
const isEmpty = computed(() => resultCount.value === 0)
const isPaginated = computed(() => requestedPage.value > 1)

/**
 * Gating d'indexation. Doit rester la MÊME condition que celle du sitemap
 * (`server/api/__sitemap__/urls.get.ts`) : une page `noindex` présente au
 * sitemap, ou l'inverse, est un signal contradictoire envoyé à Google.
 *
 * Une panne de l'API laisse `filterStats` à `null` et `totalCount` à 0, ce qui
 * ressemble à s'y méprendre à une page vide — on émettrait alors `noindex` sur
 * une page par ailleurs éligible, pour une indisponibilité de quelques minutes.
 * Un `noindex` se paie en semaines de réindexation : en cas d'erreur, on ne
 * touche pas au robots.
 */
const isThinContent = computed(() =>
  !hasError.value && resultCount.value < LINK_MIN_COUNT,
)

/**
 * Vue restreinte par le switcher de type (spec 4.74.0) : raffinement du même
 * inventaire, jamais indexable — son compte n'est pas celui sur lequel le
 * sitemap a statué. Le canonical pointe déjà l'URL nue.
 */
const isTypeRestrictedView = computed(() => accommodationType.value !== 'all')

/** Vue filtrée par voyageurs : même raisonnement que la vue restreinte par type, jamais indexée. */
const isGuestFilteredView = computed(() => Object.keys(guestApiQuery(guests.value)).length > 0)

// Page vide : le template SEO produirait « 0 chambres à partir de 0€ ».
const seoDescription = computed(() =>
  isEmpty.value
    ? t('pages.regionFilter.emptyMessage', i18nVars.value)
    : t(`pages.regionFilter.${filterI18nKey.value}.seo.description`, i18nVars.value),
)

useSeoMeta({
  title: () => t(`pages.regionFilter.${filterI18nKey.value}.seo.title`, i18nVars.value),
  description: () => seoDescription.value,
  ogTitle: () => t(`pages.regionFilter.${filterI18nKey.value}.seo.ogTitle`, i18nVars.value),
  ogDescription: () => seoDescription.value,
  ogImage: `${config.public.siteUrl}/images/og-default.jpg`,
  ogType: 'website',
  ogSiteName: 'Holicosy',
  ogUrl: () => canonicalUrl.value,
  ogLocale: ogLocale,
  robots: () => (isThinContent.value || isPaginated.value || isTypeRestrictedView.value || isGuestFilteredView.value) ? 'noindex, follow' : 'index, follow',
})

useHead({
  link: [{ rel: 'canonical', href: () => canonicalUrl.value }],
})

// --- Schema.org ---
const itemListEstablishments = computed(() => {
  return [...establishments.value]
    // `departmentCodeSlug` est requis pour bâtir l'URL de fiche : sans lui on
    // émettrait une URL à segment vide dans le JSON-LD.
    .filter(e => e.heroPhoto?.url && e.departmentCodeSlug)
    .sort((a, b) => {
      const scoreA = (a.googleRating ?? 0) * Math.log((a.googleReviewCount ?? 0) + 1)
      const scoreB = (b.googleRating ?? 0) * Math.log((b.googleReviewCount ?? 0) + 1)
      return scoreB - scoreA
    })
    .slice(0, 15)
})

useSchemaOrg([
  defineWebPage({
    '@type': 'CollectionPage',
    'inLanguage': locale.value === 'en' ? 'en-GB' : 'fr-FR',
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['#region-filter-intro'],
    },
  }),
  {
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Holicosy',
        'item': config.public.siteUrl,
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': t('common.breadcrumb.regions'),
        'item': `${config.public.siteUrl}${localePath('/regions')}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': () => regionName.value,
        'item': () => `${config.public.siteUrl}${localePrefix.value}/regions/${regionSlug.value}`,
      },
      {
        '@type': 'ListItem',
        'position': 4,
        'name': () => filterLabel.value,
        'item': () => canonicalUrl.value,
      },
    ],
  },
  ...(itemListEstablishments.value.length > 0
    ? [{
        '@type': 'ItemList' as const,
        'numberOfItems': itemListEstablishments.value.length,
        'itemListElement': itemListEstablishments.value.map((e, i) => ({
          '@type': 'ListItem' as const,
          'position': i + 1,
          'item': {
            // Spec 4.74.0 — parc mixte : typé d'après le résultat lui-même.
            // `LodgingBusiness` est le parent générique du gîte ;
            // `VacationRental` et `Product` restent interdits (doctrine SEO).
            '@type': (e.accommodationType === 'gite' ? 'LodgingBusiness' : 'BedAndBreakfast') as 'LodgingBusiness' | 'BedAndBreakfast',
            'name': e.name,
            'address': buildListingPostalAddress(e),
            'url': `${config.public.siteUrl}${localePrefix.value}/${e.departmentCodeSlug}/${e.slug}`,
            'image': e.heroPhoto!.url,
            ...(Array.isArray(e.equipments) && e.equipments.length > 0
              ? { amenityFeature: mapEquipmentsToSchemaOrg(e.equipments as string[]) }
              : {}),
            ...(e.googleRating && e.googleReviewCount
              ? {
                  aggregateRating: {
                    '@type': 'AggregateRating' as const,
                    'ratingValue': e.googleRating,
                    'bestRating': 5,
                    'reviewCount': e.googleReviewCount,
                  },
                }
              : {}),
            ...(e.priceFrom ? { priceRange: `€${e.priceFrom}` } : {}),
          },
        })),
      }]
    : []),
])

// --- Breadcrumb UI ---
const breadcrumbItems = computed(() => [
  { label: t('pages.regionFilter.breadcrumbHome'), to: localePath('/') },
  { label: t('common.breadcrumb.regions'), to: localePath('/regions') },
  { label: regionName.value, to: localePath(`/regions/${regionSlug.value}`) },
  { label: filterLabel.value },
])

// --- Barre de recherche sticky ---
const pseoZone = computed<PseoZone>(() => ({ key: 'region', value: regionName.value }))

// `type` absent = Tout pour la barre ; voyageurs absents = ses défauts.
const regionSearchFilters = computed(() =>
  pseoBarModel(pseoZone.value, searchDates.value, accommodationType.value, guests.value),
)

// Type, dates et voyageurs sur la même zone : appliqués in-page (re-fetch, URL en
// `replace`) ; une ref n'est réécrite que si sa valeur change, pour un seul fetch.
// Tout autre critère part sur /search avec l'ensemble des filtres actifs.
function handleFilterUpdate(filters: SearchFilters) {
  const update = splitPseoBarUpdate(filters, pseoZone.value, guests.value)
  if (update.local) {
    if (update.type !== accommodationType.value) onTypeChange(update.type)
    if (!sameDates(update.dates, searchDates.value)) searchDates.value = update.dates
    if (!sameGuests(update.guests, guests.value)) guests.value = update.guests
    return
  }
  navigateTo({ path: localePath('/search'), query: update.query })
}

// Ligne « Vos critères » : type, dates et voyageurs retirés sur la page via le
// traitement de la barre ; retirer la zone (ou tout effacer) part sur /search.
const { handleRemoveChip, handleClearAllChips } = usePseoCriteriaChips(regionSearchFilters, pseoZone, handleFilterUpdate)

// --- Hover / carte ---
const hoveredEstablishmentId = ref<string | null>(null)
const mapOpen = ref(false)

function handlePopupNavigate(path: string) {
  // Même query que les vignettes : dates, voyageurs et type.
  const query: Record<string, string> = { ...ficheQuery.value, from: 'map' }
  mapOpen.value = false
  navigateTo({ path, query })
}
</script>

<template>
  <div class="min-h-screen bg-neutral-50 pb-24 md:pb-0">
    <HoliSearchFilters
      :model-value="regionSearchFilters"
      @update:model-value="handleFilterUpdate"
      @navigate-to-slug="(path, query) => navigateTo({ path: localePath(path), query: Object.keys(query).length > 0 ? query : undefined })"
    />

    <HoliSearchActiveChips
      :filters="regionSearchFilters as SearchFilters"
      @remove="handleRemoveChip"
      @clear-all="handleClearAllChips"
    />

    <div class="container mx-auto px-4 py-6">
      <UBreadcrumb
        :items="breadcrumbItems"
        separator-icon="i-lucide-chevron-right"
        class="mb-4"
      />

      <section
        id="region-filter-intro"
        class="mb-8 py-6 md:py-8"
        aria-labelledby="region-filter-title"
      >
        <!-- Switcher aligné sur le titre, à droite (md+) — cf. la page
             départementale pour le motif du repli en colonne en dessous. -->
        <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div class="min-w-0">
          <div class="tag text-paper-900/60 mb-3">
            {{ regionName.toUpperCase() }} · {{ filterLabel.toUpperCase() }}
          </div>
          <h1
            id="region-filter-title"
            class="font-display font-light text-display-xs md:text-display-sm leading-[1.05] tracking-[-0.02em] text-paper-900"
          >
            {{ t(`pages.regionFilter.${filterI18nKey}.heading`, i18nVars) }}
          </h1>
          <p class="text-[15px] text-paper-900/60 mt-3">
            {{ t(`pages.regionFilter.${filterI18nKey}.subheading`, i18nVars, resultCount) }}
          </p>
        </div>

          <!-- Switcher de type d'hébergement (spec 4.74.0) : la page liste CDH ET
               gîtes. Vue `noindex` dès qu'un type est sélectionné. -->
          <div v-if="!hasError" class="shrink-0">
            <HoliSearchTypeSwitcher
              :model-value="accommodationType"
              @update:model-value="onTypeChange"
            />
          </div>
        </div>
      </section>

      <!-- Error -->
      <div v-if="hasError" class="text-center py-16">
        <UIcon name="i-lucide-alert-circle" class="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p class="text-red-600 mb-4">{{ t('pages.departments.detail.errorMessage') }}</p>
        <UButton variant="outline" class="cursor-pointer" @click="refreshSearch()">
          {{ t('pages.departments.detail.retry') }}
        </UButton>
      </div>

      <!-- Loading -->
      <div v-else-if="searchStatus === 'pending'" class="lg:grid lg:grid-cols-2 lg:gap-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <USkeleton v-for="i in 6" :key="i" class="h-80 rounded-xl" />
        </div>
        <div class="hidden lg:block">
          <USkeleton class="w-full h-[calc(100vh-200px)] rounded-xl" />
        </div>
      </div>

      <!-- Empty (dates) -->
      <div v-else-if="searchDates && establishments.length === 0" class="text-center py-16">
        <UIcon name="i-lucide-calendar-x" class="w-12 h-12 text-neutral-400 mx-auto mb-4" />
        <h2 class="font-display font-light text-2xl text-paper-900 leading-[1.1] tracking-[-0.01em] mb-2">
          {{ t('pages.departments.detail.noAvailability') }}
        </h2>
        <UButton
          :label="t('pages.departments.detail.resetDates')"
          variant="outline"
          class="mt-4 cursor-pointer"
          @click="searchDates = null"
        />
      </div>

      <!-- Empty (aucun établissement pour ce filtre dans la région) -->
      <div v-else-if="establishments.length === 0" class="text-center py-16">
        <UIcon name="i-lucide-search-x" class="w-16 h-16 text-neutral-300 mx-auto mb-4" />
        <h2 class="font-display font-light text-2xl text-paper-900 leading-[1.1] tracking-[-0.01em] mb-2">
          {{ t('pages.regionFilter.emptyTitle', i18nVars) }}
        </h2>
        <p class="text-neutral-600 mb-6 max-w-xl mx-auto">
          {{ t('pages.regionFilter.emptyMessage', i18nVars) }}
        </p>
        <UButton :to="localePath(`/regions/${regionSlug}`)" variant="outline" class="cursor-pointer">
          {{ t('pages.regionFilter.emptyAction') }}
        </UButton>
      </div>

      <!-- Résultats + carte -->
      <div v-else class="lg:grid lg:grid-cols-2 lg:gap-6">
        <div class="lg:pr-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 auto-rows-fr">
            <div
              v-for="establishment in establishments"
              :key="establishment.id"
              :data-establishment-card="establishment.id"
              class="h-full"
            >
              <HoliPropertyCardV3
                :establishment="establishment"
                :highlighted-id="hoveredEstablishmentId"
                :check-in="searchDates?.start"
                :check-out="searchDates?.end"
                :search-query="ficheQuery"
                @hover="hoveredEstablishmentId = $event"
              />
            </div>
          </div>

          <div v-if="hasMore" class="flex justify-center mt-8">
            <UButton :loading="loadingMore" size="lg" variant="outline" class="cursor-pointer" @click="loadMore">
              {{ t('pages.departments.detail.loadMore') }}
            </UButton>
          </div>
          <div v-else class="text-center mt-8 text-neutral-500 text-sm">
            {{ t('pages.departments.detail.allResultsSeen') }}
          </div>
        </div>

        <!-- `HoliSearchMapV3` cadre sur les points fournis : pas de `boundingBox`
             à passer, ce que `REGIONS` ne pourrait de toute façon pas donner. -->
        <div class="hidden lg:block lg:sticky lg:top-24 lg:h-[calc(100vh-200px)]">
          <ClientOnly>
            <HoliSearchMapV3
              :map-points="mapPoints"
              :highlighted-id="hoveredEstablishmentId"
              :global-total="totalCount"
              @marker-hover="hoveredEstablishmentId = $event"
              @popup-navigate="handlePopupNavigate"
            />
            <template #fallback>
              <USkeleton class="w-full h-full min-h-[400px] rounded-xl" />
            </template>
          </ClientOnly>
        </div>
      </div>

      <!-- Maillage : région → départements, autres filtres de la région, remontée nationale -->
      <div
        v-if="visibleDepartmentLinks.length > 0 || visibleSiblingFilters.length > 0 || showNationalLink"
        class="mt-10 pt-6 border-t border-neutral-100 space-y-8"
      >
        <EstablishmentFilterCrossDeptLinks
          :filter-slug="filterSlug"
          :links="visibleDepartmentLinks"
          :min-count="LINK_MIN_COUNT"
          :limit="12"
          :heading="t('pages.regionFilter.linksHeadingByDepartment', { filterLabel })"
        />

        <EstablishmentFilterLinks
          :base-path="`/regions/${regionSlug}`"
          i18n-namespace="pages.regionFilter"
          :counts="visibleSiblingFilters"
          :exclude-filter="filterSlug"
          :min-count="LINK_MIN_COUNT"
          :limit="8"
          :heading="t('pages.regionFilter.linksHeadingOtherFilters', { region: regionName })"
        />

        <NuxtLink
          v-if="showNationalLink"
          :to="`/chambres-hotes/${filterSlug}`"
          class="inline-flex items-center gap-2 text-sm font-medium text-neutral-700 hover:text-neutral-900 underline underline-offset-4 cursor-pointer"
        >
          <UIcon name="i-lucide-map" class="w-4 h-4" />
          {{ t('pages.regionFilter.linkToNational', { filterLabel }) }}
        </NuxtLink>
      </div>

      <!-- Bloc éditorial + FAQPage JSON-LD, sur le catalogue régional -->
      <FilterEditorial
        v-if="filterStats && filterStats.count > 0"
        :filter-slug="filterSlug"
        :filter-i18n-key="filterI18nKey"
        :region-name="regionName"
        :stats="filterStats"
        i18n-namespace="pages.regionFilter"
        class="mt-12 pt-8 border-t border-neutral-200"
      />
    </div>

    <!-- Bottom bar mobile -->
    <div
      v-if="establishments.length > 0"
      data-bottom-bar
      class="md:hidden fixed bottom-0 inset-x-0 z-[1000] bg-paper-100/95 backdrop-blur-sm border-t border-paper-300/40 px-3 py-2 safe-bottom"
    >
      <UButton
        icon="i-lucide-map"
        variant="outline"
        size="lg"
        class="w-full cursor-pointer"
        :aria-label="t('pages.regions.show.showMap')"
        @click="mapOpen = true"
      >
        {{ t('pages.regions.show.showMap') }}
      </UButton>
    </div>

    <UDrawer v-model:open="mapOpen" direction="bottom">
      <template #header>
        <div class="flex items-center justify-between w-full">
          <span class="font-semibold text-lg">{{ t('pages.regions.show.showMap') }}</span>
          <UButton
            icon="i-lucide-x"
            variant="ghost"
            size="sm"
            class="cursor-pointer"
            :aria-label="t('common.buttons.close')"
            @click="mapOpen = false"
          />
        </div>
      </template>
      <template #body>
        <div class="h-[60vh]" data-vaul-no-drag>
          <ClientOnly>
            <HoliSearchMapV3
              :map-points="mapPoints"
              :highlighted-id="hoveredEstablishmentId"
              :global-total="totalCount"
              @marker-hover="hoveredEstablishmentId = $event"
              @popup-navigate="handlePopupNavigate"
            />
            <template #fallback>
              <USkeleton class="w-full h-full min-h-[300px] rounded-xl" />
            </template>
          </ClientOnly>
        </div>
      </template>
    </UDrawer>
  </div>
</template>
